import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

const api = axios.create({
  baseURL: API_URL,
});

const ROLE_KEYS = {
  admin: { access: 'adminAccessToken', refresh: 'adminRefreshToken', profile: 'admin' },
  seller: { access: 'sellerAccessToken', refresh: 'sellerRefreshToken', profile: 'seller' },
  collector: { access: 'collectorAccessToken', refresh: 'collectorRefreshToken', profile: 'collector' },
  customer: { access: 'customerAccessToken', refresh: 'customerRefreshToken', profile: 'customer' },
};

const refreshInFlight = {};

function roleForUrl(url = '') {
  if (url.includes('/api/admin')) return 'admin';
  if (url.includes('/api/collector')) return 'collector';
  if (url.startsWith('/api/shops/')) return 'customer';
  if (url.includes('/api/seller') || url.includes('/api/orders/seller') || url.includes('/api/products')) return 'seller';
  if (url.includes('/api/auth') || url.includes('/api/orders/customer') || url.includes('/api/orders/create') || url.includes('/api/payment/') || url.includes('/api/delivery')) return 'customer';
  return null;
}

export function clearRoleSession(role) {
  const keys = ROLE_KEYS[role];
  if (!keys) return;
  localStorage.removeItem(keys.access);
  localStorage.removeItem(keys.refresh);
  localStorage.removeItem(keys.profile);
}

export async function logoutSession(role) {
  const keys = ROLE_KEYS[role];
  if (!keys) return;
  const refreshToken = localStorage.getItem(keys.refresh);
  try {
    if (refreshToken) await api.post('/api/auth/logout', { role, refreshToken }, { skipAuthRefresh: true, skipAuth: true });
  } catch (error) {
    // Local logout must complete even when the server is temporarily unavailable.
  } finally {
    clearRoleSession(role);
  }
}

// Добавляет Authorization: Bearer <token> к каждому запросу
// Роль-специфичные токены, чтобы избежать конфликтов в localStorage
api.interceptors.request.use((config) => {
  if (config.skipAuth) return config;
  let token = null;
  const role = roleForUrl(config.url || '');
  if (role) token = localStorage.getItem(ROLE_KEYS[role].access);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use((response) => response, async (error) => {
  const original = error.config;
  const role = original && roleForUrl(original.url || '');
  if (error.response?.status !== 401 || !original || original._authRetry || original.skipAuthRefresh || !role) {
    return Promise.reject(error);
  }

  const keys = ROLE_KEYS[role];
  const refreshToken = localStorage.getItem(keys.refresh);
  if (!refreshToken) {
    clearRoleSession(role);
    return Promise.reject(error);
  }

  if (!refreshInFlight[role]) {
    refreshInFlight[role] = axios.post(`${API_URL}/api/auth/refresh`, { role, refreshToken }, { skipAuthRefresh: true })
      .then((res) => {
        if (!res.data?.accessToken || !res.data?.refreshToken) throw new Error('Invalid refresh response');
        localStorage.setItem(keys.access, res.data.accessToken);
        localStorage.setItem(keys.refresh, res.data.refreshToken);
        return res.data.accessToken;
      })
      .catch((refreshError) => {
        clearRoleSession(role);
        throw refreshError;
      })
      .finally(() => { delete refreshInFlight[role]; });
  }

  try {
    const accessToken = await refreshInFlight[role];
    original._authRetry = true;
    original.headers = original.headers || {};
    original.headers.Authorization = `Bearer ${accessToken}`;
    return api(original);
  } catch (refreshError) {
    return Promise.reject(refreshError);
  }
});

// Продавцы
export const registerSeller = (data) => api.post('/api/sellers/register', data);
export const loginSeller = (data) => api.post('/api/sellers/login', data);

// Товары
export const getProducts = (params) => api.get('/api/products', { params });
export const getSellerShop = (id) => api.get(`/api/shops/${id}`, { skipAuth: true });
export const getFavoriteShops = () => api.get('/api/shops/favorites');
export const saveFavoriteShop = (id, saved) => api.put(`/api/shops/${id}/favorite`, { saved });
export const rateSellerOrder = (id, score) => api.put(`/api/shops/ratings/${id}`, { score });
export const getProduct = (id) => api.get(`/api/products/${id}`);
export const getSellerProducts = (sellerId) => api.get(`/api/products/seller/${sellerId}`);
export const addProduct = (formData) =>
  api.post('/api/products', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
export const updateProduct = (id, formData, sellerId) =>
  api.put(`/api/products/${id}?seller_id=${sellerId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
export const deleteProduct = (id, sellerId) => api.delete(`/api/products/${id}?seller_id=${sellerId}`);

// Группы товаров
export const getSellerGroups = (sellerId) => api.get(`/api/products/seller/${sellerId}/groups`);
export const createGroup = (sellerId, name, subcategory) => api.post('/api/products/seller/groups', { seller_id: sellerId, name, subcategory });
export const updateGroup = (groupId, sellerId, data) => api.put(`/api/products/seller/groups/${groupId}`, { seller_id: sellerId, ...data });
export const deleteGroup = (groupId, sellerId) => api.delete(`/api/products/seller/groups/${groupId}?seller_id=${sellerId}`);
export const getGroupProducts = (groupId) => api.get(`/api/products/group/${groupId}`);
export const getAvailableProductsForGroup = (sellerId, subcategory) => api.get(`/api/products/seller/${sellerId}/available-for-group`, { params: { subcategory } });

// Заказы
export const createOrder = (data) => api.post('/api/orders/create', data);
export const getCustomerOrders = () => api.get('/api/orders/customer');
export const getCustomerOrder = (orderId) => api.get(`/api/orders/customer/${orderId}`);
export const cancelCustomerOrder = (orderId) => api.post(`/api/orders/customer/${orderId}/cancel`);
export const createWarehouseShipment = (role, shipmentId, data) => api.post(`/api/${role === 'collector' ? 'collector' : 'admin'}/shipments/${shipmentId}/create`, data);
export const getWarehouseLabel = (role, shipmentId, size) => api.get(`/api/${role === 'collector' ? 'collector' : 'admin'}/shipments/${shipmentId}/label`, { params: { size }, responseType: 'blob' });
export const getPaymentCards = () => api.get('/api/payment/cards');
export const bindPaymentCard = (requestId, consent) => api.post('/api/payment/cards/bind', { requestId, consent });
export const getPaymentCardStatus = id => api.get(`/api/payment/cards/${id}/status`);
export const removePaymentCard = id => api.delete(`/api/payment/cards/${id}`);
export const getSellerOrders = () => api.get('/api/seller/orders');
export const getSellerOrder = (orderId) => api.get(`/api/seller/orders/${orderId}`);
export const createSellerShipment = (shipmentId, acknowledgeRealShipment) =>
  api.post(`/api/seller/orders/shipment/${shipmentId}/create`, { acknowledgeRealShipment });
export const refreshSellerShipment = (shipmentId) =>
  api.post(`/api/seller/orders/shipment/${shipmentId}/refresh`);
export const updateOrderStatus = (orderId, status) => api.patch(`/api/orders/${orderId}`, { status });
export const updateSellerOrderStatus = (orderId, status) =>
  api.patch(`/api/seller/orders/${orderId}/status`, { status });
export const getSellerFinanceSummary = () => api.get('/api/seller/finance/summary');
export const getSellerFinanceLedger = (params) => api.get('/api/seller/finance/ledger', { params });

// Варианты
export const updateVariantStock = (variantId, stock) =>
  api.patch(`/api/products/variant/${variantId}/stock`, { stock });
export const updateVariantPrice = (variantId, price) =>
  api.patch(`/api/products/variant/${variantId}/price`, { price });

// Массовое обновление
export const batchUpdateStock = (updates, sellerId) =>
  api.post('/api/products/batch-stock', { seller_id: sellerId, updates });
export const batchUpdatePrice = (updates, sellerId) =>
  api.post('/api/products/batch-price', { seller_id: sellerId, updates });

// Вспомогательные
export const generateBarcodes = (sizes) => api.post('/api/products/generate-barcodes', { sizes });
export const getBrands = () => api.get('/api/products/brands');
export const getSizes = () => api.get('/api/products/sizes');
export const getColors = () => api.get('/api/products/colors');

// Отзывы и вопросы
export const getReviews = (productId) => api.get(`/api/products/${productId}/reviews`);
export const addReview = (productId, data) => api.post(`/api/products/${productId}/reviews`, data);
export const getQuestions = (productId) => api.get(`/api/products/${productId}/questions`);
export const addQuestion = (productId, data) => api.post(`/api/products/${productId}/questions`, data);
export const answerQuestion = (questionId, data) => api.patch(`/api/products/questions/${questionId}/answer`, data);

// Доставка
export const getDeliveryProviders = () => api.get('/api/delivery/providers');
export const getPickupPoints = (data) => api.post('/api/delivery/pickup-points', data);
export const getSavedPickupPoints = () => api.get('/api/delivery/saved-points');
export const selectSavedPickupPoint = (providerId, provider = 'YANDEX') => api.post('/api/delivery/saved-points', { provider, providerId });
export const calculateDelivery = (data) => api.post('/api/delivery/calculate', data);
export const createDelivery = (data) => api.post('/api/delivery/create', data);

// Авторизация клиента
export const requestSmsCode = (phone) => api.post('/api/auth/request-code', { phone });
export const verifySmsCode = (phone, code) => api.post('/api/auth/verify-code', { phone, code });

export default api;
